import { useCallback, useRef, useState, FocusEvent } from 'react';
import { FiThumbsUp, FiThumbsDown } from 'react-icons/fi';
import { testIds, TIMEOUTS } from 'src/app/constants';
import { FeedbackType, MarkFeedbackType, FeedbackMappedType } from 'src/types/generic';
import FeedbackContainer from './FeedbackComposables';
import styles from './Feedback.module.scss';
import captureEvent from 'src/app/lib/gtm-functions';
import { useRecoilState } from 'recoil';
import { conversationsAtom, frameParamsAtom, tutorStateAtom } from 'src/app/state/atoms';
import { t } from 'i18next';
import FocusLock from 'react-focus-lock';

type FeedbackPropTypes = {
  feedback: FeedbackMappedType;
  skipNegativeModal: boolean;
  skipPositiveModal: boolean;
  markFeedback?: (options: MarkFeedbackType) => void;
  explanation?: string;
  isCorrect?: boolean | 'full' | 'partial' | 'none';
  isHistoryMode?: boolean;
  onReleaseNextQuestionGate?: () => void;
  suppressForAnnouncement?: boolean;
};

function Feedback(props: FeedbackPropTypes) {
  const {
    markFeedback,
    feedback,
    skipPositiveModal,
    skipNegativeModal,
    explanation,
    isCorrect,
    isHistoryMode,
    onReleaseNextQuestionGate,
    suppressForAnnouncement,
  } =
    props;

  const [type, setType] = useState<FeedbackType | null>(null);
  const [textResponse, setTextResponse] = useState<string | null>(null);
  const [radioResponse, setRadioSelection] = useState<string | null>(null);
  const [showAcknowlegment, setShowAcknowlegment] = useState(false);
  const [isModalOpen, setModalOpen] = useState(true);
  const [selectedLabel, setSelectedLabel] = useState<string | null>('');
  /** Controls the "Please select an option" error banner in negative feedback */
  const [showError, setShowError] = useState(false);
  const [conversations] = useRecoilState(conversationsAtom);
  const [frameParams] = useRecoilState(frameParamsAtom);
  const [tutorState] = useRecoilState(tutorStateAtom);
  const positiveFeedbackButtonRef = useRef<HTMLButtonElement | null>(null);
  const thumbsContainerRef = useRef<HTMLDivElement | null>(null);
  const isSMEMode = tutorState.isSMEMode

  const handleThumbsBlur = useCallback(
    (event: FocusEvent<HTMLDivElement>) => {
      const nextFocus = event.relatedTarget as Node | null;
      if (thumbsContainerRef.current?.contains(nextFocus)) {
        return;
      }
      onReleaseNextQuestionGate?.();
    },
    [onReleaseNextQuestionGate]
  );

  const closeModal = useCallback(
    (type = 'cancel') => {
      const _selectedLabel =
        selectedLabel !== null ? (selectedLabel !== '' ? selectedLabel : null) : null;
      // Telemetry: Click Exit Feedback
      captureEvent({
        event_action: 'Click Exit Feedback',
        event_label: _selectedLabel,
        element_status: `${typeof type === 'string' ? type : 'cancel'}`,
        event_category: 'Feedback',
        conversation_id: conversations?.data.id,
        chat_action_id: feedback?.messageId,
        mlm_book_code: frameParams?.book_code ?? 'N/A',
        app_id: 'MLMAITutor',
        exercise_id: frameParams?.exercise_id,
        user_role: frameParams?.['user-role'],
        person_id_type: frameParams?.person_id_type,
        login_session_id: frameParams?.login_session_id,
      });

      setModalOpen(false);
      setTextResponse(null);
      setRadioSelection(null);
      setShowAcknowlegment(false);
      setShowError(false);
      setType(null);
      setSelectedLabel(null);
    },
    [selectedLabel]
  );

  const feedbackServiceCall = useCallback(
    (modalSkip = false, typeParam?: FeedbackType, resetUserInputs = false) => {
      markFeedback?.({
        messageId: feedback?.messageId,
        response: resetUserInputs ? '' : textResponse,
        selection: resetUserInputs ? '' : radioResponse,
        type: typeParam ?? (type as FeedbackType),
        modalSkip,
      });
    },
    [markFeedback, feedback?.messageId, textResponse, radioResponse, type]
  );

  const getCorrectness = () => {
    if (typeof isCorrect === 'boolean') return isCorrect ? 'Correct' : 'Incorrect';

    return isCorrect === 'full'
      ? 'Correct'
      : isCorrect === 'partial'
      ? 'Partially Correct'
      : 'Incorrect';
  };

  const openModal = useCallback((type: FeedbackType) => {
    // Telemetry: Click Thumbs
    const _eventParameters: any = {
      event_action: 'Click Thumbs',
      event_label: type === 'positive' ? 'Up' : 'Down',
      event_category: 'Feedback',
      conversation_id: conversations.data.id,
      chat_action_id: feedback?.messageId,
      selected_label: selectedLabel,
      mlm_book_code: frameParams?.book_code ?? 'N/A',
      app_id: 'MLMAITutor',
      exercise_id: frameParams?.exercise_id,
      user_role: frameParams?.['user-role'],
      person_id_type: frameParams?.person_id_type,
      login_session_id: frameParams?.login_session_id,
    };
    if (explanation) {
      _eventParameters['types_of_output'] = `${getCorrectness()} explanation`;
    }
    captureEvent(_eventParameters);

    setModalOpen(true);
    setShowError(false);
    setType(type);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setRadioSelectionWrapper = useCallback((label: string) => {
    // Telemetry: Click AI feedback radial
    captureEvent({
      event_action: 'Click AI feedback radial',
      event_label: `${label}`,
      conversation_id: conversations?.data.id,
      chat_action_id: feedback?.messageId,
      mlm_book_code: frameParams?.book_code ?? 'N/A',
      app_id: 'MLMAITutor',
      exercise_id: frameParams?.exercise_id,
      user_role: frameParams?.['user-role'],
      person_id_type: frameParams?.person_id_type,
      login_session_id: frameParams?.login_session_id,
    });
    // Clear error once the user makes a selection
    setShowError(false);
    setSelectedLabel(label);
    setRadioSelection(label);
  }, []);

  /**
   * Handle the Submit action.
   * For negative feedback: if no radio option is chosen, show the error banner
   * instead of submitting. Matches the Figma validation pattern.
   */
  const handleSubmit = useCallback(() => {
    if (type === 'negative' && !radioResponse) {
      setShowError(true);
      return;
    }

    if (type === 'positive') {
      // Telemetry: Click AI feedback radial
      captureEvent({
        event_action: 'Click AI feedback radial',
        event_label: `${textResponse}`,
        conversation_id: conversations?.data.id,
        chat_action_id: feedback?.messageId,
        mlm_book_code: frameParams?.book_code ?? 'N/A',
        app_id: 'MLMAITutor',
        exercise_id: frameParams?.exercise_id,
        user_role: frameParams?.['user-role'],
        person_id_type: frameParams?.person_id_type,
        login_session_id: frameParams?.login_session_id,
      });
    }

    feedbackServiceCall();
    setShowAcknowlegment(true);
    setTimeout(() => closeModal('submit'), TIMEOUTS.FEEDBACK_MODAL_CLOSE);
  }, [type, radioResponse, textResponse, feedbackServiceCall, closeModal]);

  const hasFeedback = feedback?.selectedOption || feedback?.feedbackText;

  const isPositiveFeedback = feedback?.isPositiveMarked;
  const isNegativeFeedback = feedback?.isNegativeMarked;

  /** Title string shown in the modal header */
  const modalTitle =
    type === 'positive' ? t('FEEDBACK_POSITIVE_TITLE') : t('FEEDBACK_NEGATIVE_TITLE');

  /** Sub-heading shown below the modal title */
  const modalSubHeading =
    type === 'positive'
      ? t('FEEDBACK_POSITIVE_SUBHEADING')
      : t('FEEDBACK_NEGATIVE_SUBHEADING');

  return (
    <>
      <div className='relative'>
        {/* APAI-2290: hidden from the screen reader (and untabbable) until the
            result announcement has finished, so it is not read out first. */}
        <div
          ref={thumbsContainerRef}
          data-test-id={testIds.feedback_thumbs}
          className='flex justify-end px-space-3 gap-2'
          onBlur={handleThumbsBlur}
          aria-hidden={suppressForAnnouncement || undefined}
        >
          {/* Thumbs Up */}
          <button
            ref={positiveFeedbackButtonRef}
            className={`
              ${styles.thumbsIcon} ${feedback?.isPositiveMarked ? styles.thumbsIconActive : ''}
            `}
            onClick={() => {
              skipPositiveModal ? feedbackServiceCall(true, 'positive') : openModal('positive');
            }}
            aria-label={t('POSITIVE_FEEDBACK')}
            disabled={feedback?.disabled || isSMEMode}
          >
              <FiThumbsUp
                className={styles.icon}
                /* Inherit the gray/hover-white color from CSS */
                color='currentColor'
              />
          </button>

          {/* Thumbs Down */}
          <button
            className={`
              ${styles.thumbsIcon} ${feedback?.isNegativeMarked ? styles.thumbsIconActive : ''}
            `}
            onClick={() =>
              skipNegativeModal ? feedbackServiceCall(true, 'negative') : openModal('negative')
            }
            aria-label={t('NEGATIVE_FEEDBACK')}
            disabled={feedback?.disabled || isSMEMode}
          >
              <FiThumbsDown
                className={styles.icon}
                /* Inherit the gray/hover-white color from CSS */
                color='currentColor'
              />
          </button>
        </div>

        {/* Feedback response chat bubble for only conversation history */}
        {hasFeedback && isHistoryMode && (
          <div className='flex justify-end px-sapce-3 mt-space-2 relative'>
            <div
              className={`relative rounded-md p-space-3 max-w-md ${
                isPositiveFeedback
                  ? 'bg-green-100 border border-green-200 mr-14'
                  : isNegativeFeedback
                  ? 'bg-red-100 border border-red-200'
                  : 'bg-gray-100 border border-gray-200'
              }`}
            >
              <div
                className={`absolute top-0.5 ${
                  isPositiveFeedback ? 'right-1' : 'right-6'
                } transform -translate-y-2 rotate-45 w-3 h-3 ${
                  isPositiveFeedback
                    ? 'bg-green-100 border-t border-l border-green-200'
                    : isNegativeFeedback
                    ? 'bg-red-100 border-t border-l border-red-200'
                    : 'bg-gray-100 border-t border-l border-gray-200'
                }`}
              ></div>

              <div>
                <h4
                  className={`font-bold text-body-small mb-space-1 ${
                    isPositiveFeedback
                      ? 'text-green-800'
                      : isNegativeFeedback
                      ? 'text-red-800'
                      : 'text-gray-800'
                  }`}
                >
                  {feedback?.selectedOption ? t(feedback.selectedOption) : ''}
                </h4>
                <p
                  className={`text-label-medium ${
                    isPositiveFeedback
                      ? 'text-green-700'
                      : isNegativeFeedback
                      ? 'text-red-700'
                      : 'text-gray-600'
                  }`}
                >
                  {feedback?.feedbackText}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Feedback Modal */}
      {type && !isSMEMode && (
        <FocusLock>
          <FeedbackContainer isModalOpen={isModalOpen}>
            <FeedbackContainer.Header title={modalTitle} handleClose={closeModal} />
            {showAcknowlegment ? (
              <p className='flex flex-1 justify-center items-center font-inter-semiBold text-center' style={{ color: 'var(--text-strong)' }}>
                <span aria-live='assertive'>{t('FEEDBACK_THANKS')}</span>
              </p>
            ) : (
              <>
                <FeedbackContainer.Content
                  subHeading={modalSubHeading}
                  inputPlaceholder={
                    type === 'positive'
                      ? t('FEEDBACK_POSITIVE_PLACEHOLDER')
                      : t('FEEDBACK_NEGATIVE_PLACEHOLDER')
                  }
                  isNegativeFeedback={type === 'negative'}
                  handleFeedbackResponse={(event) => setTextResponse(event.target.value)}
                  response={textResponse}
                  handleRadioSelect={(label) => setRadioSelectionWrapper(label)}
                  selectedRadioInput={radioResponse}
                  showError={showError}
                />
                <FeedbackContainer.Footer
                  handleCancel={() => {
                    feedbackServiceCall(false, type, true);
                    closeModal();
                  }}
                  handleSubmit={handleSubmit}
                  handleSkip={() => {
                    feedbackServiceCall(true, type, true);
                    closeModal('skip');
                  }}
                />
              </>
            )}
          </FeedbackContainer>
        </FocusLock>
      )}
    </>
  );
}

export default Feedback;
